import { toIsoDate } from "@training/shared";
import type { RecordMargin, Session, SessionDetail } from "@training/shared";
import type { JournalEntry, TrackedExercise } from "./api.ts";
import { compareToPrevious, formatGap } from "./compare.ts";
import { doneCount, format, tonnage } from "./session.ts";
import { formatVolume } from "./journal.ts";

/*
 * What the home screen says beside the session to do: the records of the
 * week, how the block is kept, the loads of what comes next.
 */

/**
 * The home screen's title. "Bonsoir" from six in the evening, and still after
 * midnight: a late session is not yet the next morning. Without a first
 * name, no username in its place — "Bonjour igwall" greets nobody.
 */
export function greeting(firstName: string | null | undefined, now: Date = new Date()): string {
  const hour = now.getHours();
  const word = hour >= 18 || hour < 5 ? "Bonsoir" : "Bonjour";
  const name = firstName?.trim();
  return name ? `${word} ${name}` : word;
}

/** "+2,3 kg", "+3 reps": what a record adds, as it is said. */
export function formatMargin(margin: RecordMargin): string {
  if (margin.kind === "load") return `+${format(margin.kg)} kg`;
  return `+${margin.reps} rep${margin.reps > 1 ? "s" : ""}`;
}

/**
 * A day of the last week, the way it is spoken: "aujourd'hui", "hier", then
 * the weekday. No further back than six days, or "mardi" would not say which.
 */
export function dayLabel(iso: string, today: string = toIsoDate()): string {
  if (iso === today) return "aujourd'hui";
  const day = new Date(`${iso}T12:00:00`);
  const days = Math.round((new Date(`${today}T12:00:00`).getTime() - day.getTime()) / 86_400_000);
  if (days === 1) return "hier";
  return day.toLocaleDateString("fr-FR", { weekday: "long" });
}

/** When a past session was done: as spoken within the week, by its date beyond. */
export function whenLabel(iso: string, today: string = toIsoDate()): string {
  const days = Math.round((Date.parse(`${today}T12:00:00`) - Date.parse(`${iso}T12:00:00`)) / 86_400_000);
  if (days <= 6) return dayLabel(iso, today);
  return `le ${new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`;
}

/**
 * The week's sessions against the plan: "2 / 3 séances", and past it
 * "4 séances · 1 en plus" rather than a "4 / 3" that reads as a typo.
 */
export function weekSessionsLabel(done: number, planned: number): string {
  if (done > planned) return `${done} séances · ${done - planned} en plus`;
  return `${done} / ${planned} séance${planned > 1 ? "s" : ""}`;
}

/** The same, short, for a header: "2/3 faites", "4 faites · 1 en plus". */
export function weekDoneLabel(done: number, planned: number): string {
  if (done > planned) return `${done} faites · ${done - planned} en plus`;
  return `${done}/${planned} faite${done > 1 ? "s" : ""}`;
}

/**
 * The sessions finished since the coach's last visit, newest first. On a
 * first visit there is no last one: the past seven days stand in for it.
 */
export function sinceVisit(entries: JournalEntry[], since: string | null, now: Date = new Date()): JournalEntry[] {
  const from = since ?? new Date(now.getTime() - 7 * 86_400_000).toISOString();
  return entries.filter((entry) => entry.status === "done" && entry.finishedAt !== null && entry.finishedAt > from);
}

/** "Depuis votre dernière visite · mardi": when the news starts. */
export function sinceVisitLabel(since: string | null, today: string = toIsoDate()): string {
  return since
    ? `Depuis votre dernière visite · ${whenLabel(toIsoDate(new Date(since)), today)}`
    : "Ces 7 derniers jours";
}

/**
 * The news in one line, the sessions done without the coach first: those
 * are the ones nobody watched.
 */
export function visitHeadline(sessions: Pick<JournalEntry, "withCoach">[]): string {
  const count = sessions.length;
  const solo = sessions.filter((session) => !session.withCoach).length;
  if (count === 0) return "Aucune séance";
  const all = count > 1 ? "toutes " : "";
  const sessionsWord = `${count} séance${count > 1 ? "s" : ""}`;
  if (solo === 0) return `${sessionsWord}, ${all}avec vous`;
  if (solo === count) return `${sessionsWord}, ${all}sans vous`;
  return `${sessionsWord}, dont ${solo} sans vous`;
}

/** The latest session done, from a list the server sends newest first. */
export function lastDone(sessions: Session[]): Session | null {
  return sessions.find((session) => session.status === "done") ?? null;
}

/**
 * The few figures under the last session's comment: how long, how many
 * sets, how much lifted, how hard it felt. Each one only when it is known —
 * a session done outside the app has no clock, older ones no score.
 */
export function sessionFigures(detail: SessionDetail): string[] {
  const { session, exercises } = detail;
  const figures: string[] = [];
  if (session.startedAt && session.finishedAt) {
    const minutes = Math.round((Date.parse(session.finishedAt) - Date.parse(session.startedAt)) / 60_000);
    // A session reopened the next day is not a nine-hour workout.
    if (minutes > 0 && minutes < 4 * 60) {
      figures.push(
        minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`,
      );
    }
  }
  const sets = exercises.reduce((sum, entry) => sum + doneCount(entry), 0);
  if (sets > 0) figures.push(`${sets} série${sets > 1 ? "s" : ""}`);
  const lifted = tonnage(exercises);
  if (lifted > 0) figures.push(formatVolume(lifted));
  if (session.perceivedEffort !== null) figures.push(`difficulté ${session.perceivedEffort}/10`);
  return figures;
}

/** The movements of a session in the order they are done, the ones added on the day last. */
export function inSessionOrder<T extends Pick<TrackedExercise, "position">>(movements: T[]): T[] {
  return [...movements].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));
}

/**
 * What went up against the time before, "Fente bulgare haltère · +2,5 kg":
 * what the last session's card says when Claude has left no comment on it.
 * A card that big for one line of figures said nothing.
 */
export function progressedMovements(detail: SessionDetail): { name: string; gap: string }[] {
  return detail.exercises.flatMap((entry) => {
    const comparison = compareToPrevious(entry);
    if (comparison?.trend !== "up" || !comparison.gap) return [];
    return [{ name: entry.exercise.name, gap: formatGap(comparison.gap) }];
  });
}
